import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, ChevronDown } from "lucide-react";
import { useAuthSession } from "../../contexts/AuthSessionContext";
import { decisionRoleBadgeClassName } from "../../utils/decisionRoleBadge";
import { hasSuperAdminRole } from "../../utils/sessionModuleAccess";
import { getInitialsFromDisplayName } from "../../utils/userInitials";

export const ProfileMenu = () => {
  const { user, effectiveAccess, adminRoles, logout } = useAuthSession();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, close]);

  if (!user) {
    return null;
  }

  const displayName = user.name?.trim() || user.email || "User";
  const email = user.email ?? "";
  const roleCode = effectiveAccess?.decision_role_code ?? null;
  const primaryBu = effectiveAccess?.business_unit_codes[0] ?? "";
  const showSettings = hasSuperAdminRole(adminRoles);
  const avatarUrl = user.avatar_url?.trim();
  const initials = getInitialsFromDisplayName(user.name ?? user.email);

  return (
    <div className="flex items-center gap-4">
      <button
        type="button"
        className="flex h-9 w-9 items-center justify-center rounded transition-colors hover:bg-gray-100"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5 text-gray-600" />
      </button>

      <div className="relative" ref={rootRef}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 rounded-lg py-1 pl-1 pr-2 transition-colors hover:bg-gray-100"
          aria-expanded={open}
          aria-haspopup="menu"
        >
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt=""
              className="h-9 w-9 rounded-full object-cover"
            />
          ) : (
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 text-xs font-semibold text-white">
              {initials}
            </span>
          )}
          <ChevronDown className="h-4 w-4 text-gray-500" />
        </button>

        {open && (
          <div
            className="absolute right-0 z-50 mt-2 w-72 rounded-xl border border-gray-200 bg-white py-2 shadow-lg"
            role="menu"
          >
            <div className="border-b border-gray-100 px-4 pb-3 pt-1">
              <p className="text-sm font-semibold text-gray-900">{displayName}</p>
              {email ? (
                <p className="mt-0.5 truncate text-xs text-gray-500">{email}</p>
              ) : null}
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {roleCode ? (
                  <span
                    className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${decisionRoleBadgeClassName(roleCode)}`}
                  >
                    {roleCode}
                  </span>
                ) : null}
                {primaryBu ? (
                  <span className="inline-flex rounded border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-700">
                    {primaryBu}
                  </span>
                ) : null}
              </div>
            </div>
            <button
              type="button"
              role="menuitem"
              className="block w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50"
              onClick={() => {
                close();
                navigate("/profile");
              }}
            >
              Profile
            </button>
            {showSettings ? (
              <button
                type="button"
                role="menuitem"
                className="block w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => {
                  close();
                  navigate("/authorization-management");
                }}
              >
                Settings
              </button>
            ) : null}
            <button
              type="button"
              role="menuitem"
              className="block w-full px-4 py-2.5 text-left text-sm font-medium text-red-600 hover:bg-red-50"
              onClick={() => {
                close();
                void logout();
              }}
            >
              Logout
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
